/**
 * SearchFilterBar Component
 *
 * Search input with filter chips for Content Hub.
 * Provides client-side filtering capabilities.
 */

import { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Debounce hook for search
const useDebounce = (callback, delay) => {
  const [timeoutId, setTimeoutId] = useState(null);

  return useCallback(
    (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      const newTimeoutId = setTimeout(() => {
        callback(...args);
      }, delay);
      setTimeoutId(newTimeoutId);
    },
    [callback, delay, timeoutId]
  );
};

// Animation variants
const chipVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: { scale: 1, opacity: 1 },
  exit: { scale: 0.8, opacity: 0 },
};

// Filter configurations for each tab
const filterConfigs = {
  rss: [
    { key: "all", label: "All", icon: "apps" },
    { key: "today", label: "Today", icon: "today" },
    { key: "week", label: "This Week", icon: "date_range" },
    { key: "rss", label: "RSS", icon: "rss_feed" },
    { key: "newsapi", label: "News API", icon: "newspaper" },
    { key: "reddit", label: "Reddit", icon: "forum" },
  ],
  drafts: [
    { key: "all", label: "All", icon: "apps" },
    { key: "recent", label: "Recent", icon: "schedule" },
    { key: "oldest", label: "Oldest", icon: "history" },
  ],
  posted: [
    { key: "all", label: "All", icon: "apps" },
    { key: "today", label: "Today", icon: "today" },
    { key: "week", label: "This Week", icon: "date_range" },
    { key: "month", label: "This Month", icon: "calendar_month" },
  ],
  rejected: [
    { key: "all", label: "All", icon: "apps" },
    { key: "recent", label: "Recent", icon: "schedule" },
  ],
};

function SearchFilterBar({
  tab = "rss",
  searchQuery = "",
  onSearchChange,
  activeFilter = "all",
  onFilterChange,
  resultCount = 0,
  totalCount = 0,
}) {
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const filters = filterConfigs[tab] || filterConfigs.rss;

  // Debounced search handler
  const debouncedSearch = useDebounce((value) => {
    onSearchChange?.(value);
  }, 300);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalQuery(value);
    debouncedSearch(value);
  };

  const handleClearSearch = () => {
    setLocalQuery("");
    onSearchChange?.("");
  };

  const hasActiveFilters = localQuery.length > 0 || activeFilter !== "all";
  const showResultCount = hasActiveFilters && totalCount > 0;

  return (
    <MDBox mb={3}>
      {/* Search Bar */}
      <MDBox
        sx={{
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: ({ boxShadows }) => boxShadows.sm,
          p: { xs: 2, sm: 2.5 },
        }}
      >
        <TextField
          fullWidth
          placeholder="Search content..."
          value={localQuery}
          onChange={handleSearchChange}
          variant="outlined"
          size="medium"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon sx={{ color: "text.secondary" }}>search</Icon>
              </InputAdornment>
            ),
            endAdornment: localQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  sx={{
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <Icon fontSize="small">close</Icon>
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: "#F7F9FC",
              transition: "all 0.2s ease",
              "& fieldset": {
                borderColor: "transparent",
              },
              "&:hover fieldset": {
                borderColor: "rgba(26, 115, 232, 0.3)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#1A73E8",
                borderWidth: "2px",
              },
              "&.Mui-focused": {
                backgroundColor: "white",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: { xs: "0.9rem", sm: "1rem" },
              py: 1.5,
            },
          }}
        />

        {/* Filter Chips */}
        <MDBox mt={2} display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <AnimatePresence mode="popLayout">
            {filters.map((filter) => (
              <motion.div
                key={filter.key}
                variants={chipVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
              >
                <Chip
                  icon={<Icon sx={{ fontSize: "18px !important" }}>{filter.icon}</Icon>}
                  label={filter.label}
                  onClick={() => onFilterChange?.(filter.key)}
                  variant={activeFilter === filter.key ? "filled" : "outlined"}
                  sx={{
                    borderRadius: "20px",
                    fontWeight: activeFilter === filter.key ? 600 : 400,
                    fontSize: "0.85rem",
                    py: 2.5,
                    px: 0.5,
                    transition: "all 0.2s ease",
                    backgroundColor: activeFilter === filter.key ? "#1A73E8" : "transparent",
                    color: activeFilter === filter.key ? "white" : "text.primary",
                    borderColor: activeFilter === filter.key ? "#1A73E8" : "rgba(0, 0, 0, 0.12)",
                    "& .MuiChip-icon": {
                      color: activeFilter === filter.key ? "white" : "text.secondary",
                    },
                    "&:hover": {
                      backgroundColor:
                        activeFilter === filter.key ? "#1557B0" : "rgba(26, 115, 232, 0.08)",
                      borderColor: "#1A73E8",
                    },
                    // Better touch targets for mobile
                    minHeight: { xs: "36px", sm: "32px" },
                    "& .MuiChip-label": {
                      px: { xs: 1.5, sm: 1 },
                    },
                  }}
                />
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Result Count */}
          {showResultCount && (
            <MDBox
              component={motion.div}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              ml="auto"
              sx={{ display: { xs: "none", sm: "block" } }}
            >
              <MDTypography variant="caption" color="text" fontWeight="medium">
                {resultCount} of {totalCount} items
              </MDTypography>
            </MDBox>
          )}
        </MDBox>

        {/* Mobile Result Count - Separate row */}
        {showResultCount && (
          <MDBox
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            mt={1.5}
            sx={{ display: { xs: "block", sm: "none" } }}
          >
            <MDBox display="flex" alignItems="center" justifyContent="space-between">
              <MDTypography variant="caption" color="text" fontWeight="medium">
                Showing {resultCount} of {totalCount} items
              </MDTypography>
              {(localQuery || activeFilter !== "all") && (
                <MDBox
                  component="button"
                  onClick={() => {
                    handleClearSearch();
                    onFilterChange?.("all");
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: "rgba(244, 67, 53, 0.08)",
                    color: "#F44335",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(244, 67, 53, 0.15)",
                    },
                  }}
                >
                  <Icon sx={{ fontSize: "0.9rem !important" }}>filter_alt_off</Icon>
                  Clear
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        )}

        {/* Active Filters Summary */}
        {(localQuery || activeFilter !== "all") && (
          <MDBox
            component={motion.div}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            mt={2}
            p={1.5}
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              gap: 1,
              borderRadius: "10px",
              backgroundColor: "#F0F9FF",
              border: "1px solid #BAE6FD",
            }}
          >
            <Icon sx={{ color: "#0EA5E9", fontSize: "1.1rem !important" }}>filter_list</Icon>
            <MDTypography variant="caption" sx={{ color: "#0369A1", fontWeight: 500 }}>
              Filtering by:
              {localQuery && <strong> &quot;{localQuery}&quot;</strong>}
              {localQuery && activeFilter !== "all" && " + "}
              {activeFilter !== "all" && (
                <strong>{filters.find((f) => f.key === activeFilter)?.label}</strong>
              )}
            </MDTypography>
            <MDBox
              component="button"
              onClick={() => {
                handleClearSearch();
                onFilterChange?.("all");
              }}
              sx={{
                ml: "auto",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: "6px",
                border: "none",
                backgroundColor: "transparent",
                color: "#0EA5E9",
                fontSize: "0.75rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(14, 165, 233, 0.1)",
                },
              }}
            >
              <Icon sx={{ fontSize: "0.85rem !important" }}>close</Icon>
              Clear all
            </MDBox>
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}

SearchFilterBar.propTypes = {
  tab: PropTypes.oneOf(["rss", "drafts", "posted", "rejected"]),
  searchQuery: PropTypes.string,
  onSearchChange: PropTypes.func,
  activeFilter: PropTypes.string,
  onFilterChange: PropTypes.func,
  resultCount: PropTypes.number,
  totalCount: PropTypes.number,
};

export default SearchFilterBar;

/**
 * Hook for filtering content client-side
 */
export function useContentFilter(items = [], searchQuery = "", activeFilter = "all") {
  return useMemo(() => {
    let filtered = [...items];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const title = (item.title || item.sourceHeadline || "").toLowerCase();
        const description = (item.description || item.sourceSummary || "").toLowerCase();
        const channel = (item.sourceChannel || "").toLowerCase();
        return title.includes(query) || description.includes(query) || channel.includes(query);
      });
    }

    // Apply source filter (RSS, News API, Reddit)
    const sourceFilters = ["rss", "newsapi", "reddit"];
    if (sourceFilters.includes(activeFilter)) {
      filtered = filtered.filter((item) => {
        const source = (item.source || item.sourceChannel || "").toLowerCase();
        switch (activeFilter) {
          case "rss":
            return source.includes("rss") || source.includes("feed");
          case "newsapi":
            return source.includes("news") || source.includes("api");
          case "reddit":
            return source.includes("reddit");
          default:
            return true;
        }
      });
    }

    // Apply date/time filter
    if (activeFilter !== "all" && !sourceFilters.includes(activeFilter)) {
      const now = new Date();
      // Use last 24 hours for "today" instead of strict calendar day
      const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - 7);
      const startOfMonth = new Date(now);
      startOfMonth.setDate(now.getDate() - 30);

      filtered = filtered.filter((item) => {
        const dateValue = item.date || item.created_at || item.createdAt;
        // If no valid date, exclude from date-based filters
        if (!dateValue) return false;
        const itemDate = new Date(dateValue);
        // Check for invalid date
        if (isNaN(itemDate.getTime())) return false;

        switch (activeFilter) {
          case "today":
            // Last 24 hours
            return itemDate >= last24Hours;
          case "week":
            // Last 7 days
            return itemDate >= startOfWeek;
          case "month":
            // Last 30 days
            return itemDate >= startOfMonth;
          case "recent":
            // Sort by newest first (already done by default from API)
            return true;
          case "oldest":
            // This needs special handling - will be sorted later
            return true;
          default:
            return true;
        }
      });

      // Handle sorting for recent/oldest
      if (activeFilter === "oldest") {
        filtered.sort((a, b) => {
          const dateA = new Date(a.date || a.created_at || a.createdAt);
          const dateB = new Date(b.date || b.created_at || b.createdAt);
          return dateA - dateB;
        });
      } else if (activeFilter === "recent") {
        filtered.sort((a, b) => {
          const dateA = new Date(a.date || a.created_at || a.createdAt);
          const dateB = new Date(b.date || b.created_at || b.createdAt);
          return dateB - dateA;
        });
      }
    }

    return filtered;
  }, [items, searchQuery, activeFilter]);
}
